// src/utils/globalErrorMonitor.js
// Filet de sécurité : toute erreur JavaScript non gérée (hors des try/catch et des boundaries React)
// est remontée dans `appLogs` — js_unhandled_error (error) et js_unhandled_rejection (warning),
// avec appareil/navigateur/build. Sans ça, un crash hors portée n'est visible de personne.
import MonitoringService from '../services/MonitoringService';
import { preAuthLog } from '../services/PreAuthLogService';
import { auth } from '../firebaseConfig';
import { deviceInfo } from './staveRenderMonitor';

const MAX_PER_SESSION = 15;
const IGNORED_MESSAGES = [
  /ResizeObserver loop/i,                 // bénin, déclenché par les observers de mise en page
  /^Script error\.?$/i,                   // erreur cross-origin sans détail exploitable
  /Non-Error promise rejection captured/i,
];
const EXTENSION_SCHEME = /(chrome|moz|safari|safari-web)-extension:/i;

export function scrub(text, max = 300) {
  return String(text ?? '')
    .replace(/\S+@\S+\.\S+/g, '<email>')
    .replace(/[A-Za-z0-9_-]{28,}/g, '<id>') // uid Firebase, tokens, ids de documents
    .slice(0, max);
}

// 4 premières lignes de la pile, sans origine ni query string (garde fichier:ligne:colonne).
export function topFrames(stack) {
  return String(stack || '').split('\n').slice(0, 4)
    .map(l => l.trim().replace(/https?:\/\/[^/\s)]+/g, '').replace(/\?[^\s):]*/g, ''))
    .join(' | ').slice(0, 500);
}

export function shouldIgnore(message, stack, filename) {
  if (IGNORED_MESSAGES.some(re => re.test(message || ''))) return true;
  return EXTENSION_SCHEME.test(`${filename || ''} ${stack || ''}`); // extensions du navigateur
}

let count = 0;
const seen = new Set();

// Avant connexion, `appLogs` est refusé par les règles : on bascule sur `preAuthLogs` (anonyme, borné).
export function emitMonitoringEvent(level, type, params) {
  const uid = auth.currentUser?.uid || null;
  if (uid) MonitoringService[level](type, params, uid);
  else preAuthLog(level, type, params);
}

function report(level, type, message, stack, extra) {
  try {
    if (count >= MAX_PER_SESSION) return;
    const msg = scrub(message);
    const page = window.location.pathname;
    const key = `${type}|${page}|${msg}`;
    if (seen.has(key)) return;
    seen.add(key);
    count += 1;
    const params = {
      exerciseId: `${page}|${msg.slice(0, 60)}`, // clé de dédoublonnage du MonitoringService
      errorMessage: msg,
      stack: topFrames(stack),
      page,
      chunkLoad: /Loading chunk|ChunkLoadError|dynamically imported module/i.test(msg),
      ...extra,
      ...deviceInfo(),
    };
    emitMonitoringEvent(level, type, params);
  } catch { /* le monitoring ne doit jamais casser l'app */ }
}

export function installGlobalErrorMonitor() {
  window.addEventListener('error', (e) => {
    // Les erreurs de chargement de ressources (img, script) n'ont pas de message : ignorées ici.
    if (!e.message) return;
    const stack = e.error?.stack || '';
    if (shouldIgnore(e.message, stack, e.filename)) return;
    report('error', 'js_unhandled_error', e.message, stack, { line: e.lineno, col: e.colno });
  });

  // Ressources qui ne chargent pas (image, script, son, feuille de style) : l'événement 'error' ne
  // remonte pas (bubbles=false), on l'attrape en phase de capture. Souvent cause d'un son muet ou
  // d'une icône manquante chez un seul élève (réseau, bloqueur de pub, mauvais déploiement).
  const RESOURCE_TAGS = new Set(['IMG', 'SCRIPT', 'LINK', 'AUDIO', 'VIDEO', 'SOURCE']);
  let resourceCount = 0;
  window.addEventListener('error', (e) => {
    try {
      const t = e.target;
      if (!t || t === window || !t.tagName || !RESOURCE_TAGS.has(t.tagName)) return;
      const raw = t.currentSrc || t.src || t.href || '';
      if (!raw || EXTENSION_SCHEME.test(raw) || resourceCount >= 10) return;
      const url = raw.replace(/^https?:\/\/[^/]+/, '').replace(/[?#].*$/, '').slice(0, 200);
      const key = `resource|${url}`;
      if (seen.has(key)) return;
      seen.add(key);
      resourceCount += 1;
      emitMonitoringEvent('warning', 'resource_load_failed', {
        exerciseId: `${window.location.pathname}|${url}`,
        tag: t.tagName.toLowerCase(),
        url,
        page: window.location.pathname,
        ...deviceInfo(),
      });
    } catch { /* silencieux */ }
  }, true);

  window.addEventListener('unhandledrejection', (e) => {
    const r = e.reason;
    const message = r?.message || (typeof r === 'string' ? r : 'rejection sans message');
    const stack = r?.stack || '';
    if (shouldIgnore(message, stack, '')) return;
    report('warning', 'js_unhandled_rejection', message, stack, { code: scrub(r?.code, 60) || null });
  });
}
